import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import ProductCard from '../components/product/ProductCard';
import ImageGallery from '../components/product/ImageGallery';
import ReviewList from '../components/review/ReviewList';
import ReviewForm from '../components/review/ReviewForm';
import StarRating from '../components/review/StarRating';
import { getImageUrl } from '../utils/imageUrl';
import { Product, Review } from '../types';
import { productAPI, reviewAPI } from '../services/api';
import { trackFacebookEvent } from '../utils/facebookPixel';
import { gtmEvents } from '../utils/gtmDataLayer';

import './ProductDetails.css';

const ProductDetails: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { addItem: addToWishlist, removeItem: removeFromWishlist, isInWishlist } = useWishlist();
  const [product, setProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);

  // Selected attribute options & validation error state
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedWeight, setSelectedWeight] = useState<string>('');
  const [missingAttributes, setMissingAttributes] = useState<{ color?: boolean; size?: boolean; weight?: boolean }>({});

  const validateVariantSelection = (): boolean => {
    if (!product) return false;

    const hasColor = !!(product.attributes?.colors?.enabled && (product.attributes.colors.options || []).length > 0);
    const hasSize = !!(product.attributes?.sizes?.enabled && (product.attributes.sizes.options || []).length > 0);
    const hasWeight = !!(product.attributes?.weights?.enabled && (product.attributes.weights.options || []).length > 0);

    const missingColor = hasColor && !selectedColor;
    const missingSize = hasSize && !selectedSize;
    const missingWeight = hasWeight && !selectedWeight;

    if (missingColor || missingSize || missingWeight) {
      setMissingAttributes({
        color: missingColor,
        size: missingSize,
        weight: missingWeight,
      });

      const attrSection = document.querySelector('.product-attributes');
      if (attrSection) {
        attrSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return false;
    }

    setMissingAttributes({});
    return true;
  };

  const handleAddToCart = (event?: React.MouseEvent<HTMLElement>) => {
    if (!validateVariantSelection()) return;
    if (product) {
      if (event?.currentTarget) {
        runAddToCartAnimation(event.currentTarget);
      }

      addItem(product, quantity, { color: selectedColor, size: selectedSize, weight: selectedWeight });

      // Track AddToCart
      trackFacebookEvent('AddToCart', {
        content_name: currentLang === 'bn' ? product.name_bn : product.name_en,
        content_ids: [product.id.toString()],
        content_type: 'product',
        value: product.final_price * quantity,
        currency: 'BDT'
      });
    }
  };

  const handleDirectOrder = () => {
    if (!validateVariantSelection()) return;
    if (product) {
      // Track AddToCart (since direct order implies adding to cart context)
      trackFacebookEvent('AddToCart', {
        content_name: currentLang === 'bn' ? product.name_bn : product.name_en,
        content_ids: [product.id.toString()],
        content_type: 'product',
        value: product.final_price * quantity,
        currency: 'BDT'
      });

      // Store complete product data in sessionStorage (no need to fetch again)
      const orderData = {
        product: product,
        quantity: quantity,
        selected_color: selectedColor,
        selected_size: selectedSize,
        selected_weight: selectedWeight,
      };
      sessionStorage.setItem('directOrder', JSON.stringify(orderData));
      navigate('/checkout');
    }
  };

  useEffect(() => {
    const loadProduct = async () => {
      try {
        setLoading(true);
        const data = await productAPI.getProductBySlug(slug!);
        setProduct(data);
      } catch (error) {
        console.error('Error loading product:', error);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadProduct();
    }
  }, [slug]);

  const loadReviews = async (productId: number) => {
    try {
      setReviewsLoading(true);
      const data = await reviewAPI.getProductReviews(productId);
      // Ensure data is always an array
      const reviewsArray = Array.isArray(data) ? data : ((data as any)?.results || (data as any)?.data || []);
      setReviews(reviewsArray);
    } catch (error) {
      console.error('Error loading reviews:', error);
      setReviews([]); // Set empty array on error
    } finally {
      setReviewsLoading(false);
    }
  };

  useEffect(() => {
    if (product) {
      loadReviews(product.id);
    }
  }, [product]);

  // Reset attribute selections when product changes
  useEffect(() => {
    setSelectedColor('');
    setSelectedSize('');
    setSelectedWeight('');
  }, [product?.id]);

  const currentLang = i18n.language;

  useEffect(() => {
    if (product) {
      const productName = currentLang === 'bn' ? product.name_bn : product.name_en;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const categoryName = (product as any).category ? (currentLang === 'bn' ? (product as any).category.name_bn : (product as any).category.name_en) : '';

      // Track Facebook ViewContent
      trackFacebookEvent('ViewContent', {
        content_name: productName,
        content_ids: [product.id.toString()],
        content_type: 'product',
        value: product.final_price,
        currency: 'BDT',
        content_category: categoryName
      });

      // Track GTM/GA4 view_item
      gtmEvents.viewItem({
        id: product.id,
        name: productName,
        price: product.final_price,
        category: categoryName,
      });
    }
  }, [product, currentLang]);

  const runAddToCartAnimation = (sourceElement: HTMLElement) => {
    if (!product || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const targetElement = Array.from(
      document.querySelectorAll<HTMLElement>(
        '.mobile-bottom-navigation .mobile-cart-btn, .header-user-actions .cart-btn-top'
      )
    ).find((element) => {
      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && window.getComputedStyle(element).display !== 'none';
    });
    if (!targetElement) return;

    const sourceRect = sourceElement.getBoundingClientRect();
    const targetRect = targetElement.getBoundingClientRect();
    const startX = sourceRect.left + sourceRect.width / 2;
    const startY = sourceRect.top + sourceRect.height / 2;
    const endX = targetRect.left + targetRect.width / 2;
    const endY = targetRect.top + targetRect.height / 2;

    const flyer = document.createElement('div');
    flyer.className = 'fly-to-cart-animation';

    const imageUrl = getImageUrl(
      product.image ||
      product.primary_image ||
      product.images?.find(img => img.is_primary)?.image ||
      product.images?.[0]?.image
    );
    if (imageUrl) {
      flyer.style.backgroundImage = `url(${imageUrl})`;
    } else {
      flyer.classList.add('without-image');
    }

    flyer.style.left = `${startX - 26}px`;
    flyer.style.top = `${startY - 26}px`;
    flyer.style.setProperty('--fly-x', `${endX - startX}px`);
    flyer.style.setProperty('--fly-y', `${endY - startY}px`);
    document.body.appendChild(flyer);
    flyer.classList.add('is-flying');

    window.setTimeout(() => {
      targetElement.classList.add('cart-target-bump');
    }, 820);

    window.setTimeout(() => {
      flyer.remove();
      targetElement.classList.remove('cart-target-bump');
    }, 1180);
  };

  const handleWishlistToggle = () => {
    if (product) {
      const inWishlist = isInWishlist(product.id);
      if (inWishlist) {
        removeFromWishlist(product.id);
        Swal.fire({
          position: 'top-end',
          icon: 'info',
          title: i18n.language === 'bn' ? 'অপসারিত' : 'Removed',
          text: t('common.removed') || 'Removed from Wishlist',
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
          toast: true,
        });
      } else {
        addToWishlist(product);

        // Track AddToWishlist
        trackFacebookEvent('AddToWishlist', {
          content_name: currentLang === 'bn' ? product.name_bn : product.name_en,
          content_ids: [product.id.toString()],
          content_type: 'product',
          value: product.final_price,
          currency: 'BDT'
        });

        Swal.fire({
          position: 'top-end',
          icon: 'success',
          title: i18n.language === 'bn' ? 'যোগ হয়েছে' : 'Added',
          text: t('common.added') || 'Added to Wishlist',
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false,
          toast: true,
        });
      }
    }
  };

  const handleQuantityChange = (delta: number) => {
    setQuantity(prev => Math.max(1, Math.min(product?.stock || 1, prev + delta)));
  };

  // Helper function to check if HTML content is actually empty
  const isHtmlEmpty = (html: string | undefined | null): boolean => {
    if (!html || html.trim() === '') return true;

    // Remove HTML tags and check if there's actual content
    const text = html.replace(/<[^>]*>/g, '').trim();
    return text === '';
  };

  if (loading) {
    return <div className="loading-container"><p>{t('common.loading')}</p></div>;
  }

  if (!product) {
    return <div className="error-container"><p>{t('common.error')}</p></div>;
  }

  const productName = currentLang === 'bn' ? product.name_bn : product.name_en;
  const productDesc = currentLang === 'bn' ? product.description_bn : product.description_en;
  const productSummary = currentLang === 'bn' ? product.summary_bn : product.summary_en;
  const hasSummaryContent = !isHtmlEmpty(productSummary);

  // Get category name - handle both nested category object and category_name string
  const categoryName = typeof product.category === 'object' && product.category !== null
    ? (currentLang === 'bn' ? product.category.name_bn : product.category.name_en)
    : product.category_name || '';

  // Combine main image with gallery images
  const mainImage = product.image ? [{
    id: -1, // Use -1 or distinct ID for main image
    image: getImageUrl(product.image),
    is_primary: true,
    display_order: 0
  }] : [];

  const galleryImages = [
    ...mainImage,
    ...(product.images && product.images.length > 0
      ? product.images.map(img => ({ ...img, image: getImageUrl(img.image) }))
      : [])
  ];

  return (
    <div className="product-details-page">
      <div className="container">
        <div className="breadcrumb">
          <Link to="/">{t('nav.home')}</Link>
          <span>/</span>
          <Link to="/products">{t('nav.products')}</Link>
          <span>/</span>
          <span className="current">{productName}</span>
        </div>

        <div className="product-details-layout">
          <div className="product-gallery">
            <ImageGallery images={galleryImages} productName={productName} />
          </div>

          <div className="product-info">
            <div className="product-header">
              <h1 className="product-title">{productName}</h1>
              <button
                className={`wishlist-btn ${isInWishlist(product.id) ? 'active' : ''}`}
                onClick={handleWishlistToggle}
                aria-label="Add to Wishlist"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </button>
            </div>

            <div className="product-pricing">
              <div className="price-row">
                {product.discount_price ? (
                  <>
                    <span className="current-price">৳{product.discount_price}</span>
                    <span className="original-price">৳{product.price}</span>
                    <span className="discount-badge">{product.discount_percentage}%</span>
                  </>
                ) : (
                  <span className="current-price">৳{product.price}</span>
                )}
              </div>
              {reviews.length > 0 && (
                <div className="price-rating-display">
                  <StarRating
                    rating={reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length}
                    readOnly
                    size="small"
                  />
                  <span className="rating-count">
                    ({(reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1)} {currentLang === 'bn' ? 'এর মধ্যে' : 'out of'} 5)
                  </span>
                </div>
              )}
              {/* Free delivery badge — only shown when active */}
              {product.free_delivery && (
                <div className="free-delivery-badge active">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="3" width="15" height="13" />
                    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                    <circle cx="5.5" cy="18.5" r="2.5" />
                    <circle cx="18.5" cy="18.5" r="2.5" />
                  </svg>
                  <span>{currentLang === 'bn' ? 'ফ্রি হোম ডেলিভারি' : 'Free Home Delivery'}</span>
                </div>
              )}
            </div>

            <div className="product-stock">
              {product.in_stock ? (
                <span className="in-stock">✓ {t('product.inStock')}</span>
              ) : (
                <span className="out-of-stock">✗ {t('product.outOfStock')}</span>
              )}
            </div>

            <div className="product-summary">
              <h3>{currentLang === 'bn' ? 'সংক্ষিপ্ত বিবরণ' : 'Summary'}</h3>
              {hasSummaryContent ? (
                <div
                  className="summary-text"
                  dangerouslySetInnerHTML={{
                    __html: productSummary || ''
                  }}
                />
              ) : (
                <ul className="product-summary-list">
                  <li>
                    <span className="summary-label">{currentLang === 'bn' ? 'বিভাগ:' : 'Category:'}</span>
                    <span className="summary-value">{categoryName}</span>
                  </li>
                  <li>
                    <span className="summary-label">{currentLang === 'bn' ? 'স্টক:' : 'Stock:'}</span>
                    <span className="summary-value">
                      {product.in_stock
                        ? (currentLang === 'bn' ? 'স্টকে আছে' : 'In Stock')
                        : (currentLang === 'bn' ? 'স্টক নেই' : 'Out of Stock')
                      }
                    </span>
                  </li>
                  {product.discount_percentage > 0 && (
                    <li>
                      <span className="summary-label">{currentLang === 'bn' ? 'ছাড়:' : 'Discount:'}</span>
                      <span className="summary-value">{product.discount_percentage}%</span>
                    </li>
                  )}
                </ul>
              )}
            </div>

            {product.in_stock && (
              <div className="product-actions">
                {/* Attribute Selectors — only render if at least one attribute has options */}
                {(
                  !!(product.attributes?.colors?.enabled && (product.attributes.colors.options || []).length > 0) ||
                  !!(product.attributes?.sizes?.enabled && (product.attributes.sizes.options || []).length > 0) ||
                  !!(product.attributes?.weights?.enabled && (product.attributes.weights.options || []).length > 0)
                ) && (
                    <div className="product-attributes">
                      {/* Color selector */}
                      {product.attributes.colors?.enabled && (product.attributes.colors.options || []).length > 0 && (
                        <div className={`attr-selector-group ${missingAttributes.color ? 'attr-error' : ''}`}>
                          <span className="attr-selector-label">
                            {currentLang === 'bn' ? 'রং সিলেক্ট করুন' : 'Select Color'}
                            {selectedColor ? (
                              <span className="attr-selected-value"> — {selectedColor}</span>
                            ) : missingAttributes.color ? (
                              <span className="attr-required-badge">{currentLang === 'bn' ? '* আবশ্যক' : '* Required'}</span>
                            ) : null}
                          </span>
                          <div className="attr-color-swatches">
                            {(product.attributes.colors.options || []).map(color => (
                              <button
                                key={color}
                                type="button"
                                title={color}
                                className={`color-swatch-btn ${selectedColor === color ? 'selected' : ''}`}
                                onClick={() => {
                                  setSelectedColor(prev => prev === color ? '' : color);
                                  setMissingAttributes(prev => ({ ...prev, color: false }));
                                }}
                              >
                                <span
                                  className="color-swatch-dot"
                                  style={{ background: color.toLowerCase() }}
                                ></span>
                                <span className="color-swatch-name">{color}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Size selector */}
                      {product.attributes.sizes?.enabled && (product.attributes.sizes.options || []).length > 0 && (
                        <div className={`attr-selector-group ${missingAttributes.size ? 'attr-error' : ''}`}>
                          <span className="attr-selector-label">
                            {currentLang === 'bn' ? 'সাইজ সিলেক্ট করুন' : 'Select Size'}
                            {selectedSize ? (
                              <span className="attr-selected-value"> — {selectedSize}</span>
                            ) : missingAttributes.size ? (
                              <span className="attr-required-badge">{currentLang === 'bn' ? '* আবশ্যক' : '* Required'}</span>
                            ) : null}
                          </span>
                          <div className="attr-chips-row">
                            {(product.attributes.sizes.options || []).map(size => (
                              <button
                                key={size}
                                type="button"
                                className={`attr-chip-btn size-btn ${selectedSize === size ? 'selected' : ''}`}
                                onClick={() => {
                                  setSelectedSize(prev => prev === size ? '' : size);
                                  setMissingAttributes(prev => ({ ...prev, size: false }));
                                }}
                              >
                                {size}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Weight selector */}
                      {product.attributes.weights?.enabled && (product.attributes.weights.options || []).length > 0 && (
                        <div className={`attr-selector-group ${missingAttributes.weight ? 'attr-error' : ''}`}>
                          <span className="attr-selector-label">
                            {currentLang === 'bn' ? 'ওজন সিলেক্ট করুন' : 'Select Weight'}
                            {selectedWeight ? (
                              <span className="attr-selected-value"> — {selectedWeight}</span>
                            ) : missingAttributes.weight ? (
                              <span className="attr-required-badge">{currentLang === 'bn' ? '* আবশ্যক' : '* Required'}</span>
                            ) : null}
                          </span>
                          <div className="attr-chips-row">
                            {(product.attributes.weights.options || []).map(weight => (
                              <button
                                key={weight}
                                type="button"
                                className={`attr-chip-btn weight-btn ${selectedWeight === weight ? 'selected' : ''}`}
                                onClick={() => {
                                  setSelectedWeight(prev => prev === weight ? '' : weight);
                                  setMissingAttributes(prev => ({ ...prev, weight: false }));
                                }}
                              >
                                {weight}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                <div className="quantity-selector">
                  <label className="quantity-label">{currentLang === 'bn' ? 'পরিমান' : 'Select Quantity'}</label>
                  <div className="quantity-controls">
                    <button onClick={() => handleQuantityChange(-1)} aria-label="Decrease quantity">−</button>
                    <span>{quantity}</span>
                    <button onClick={() => handleQuantityChange(1)} aria-label="Increase quantity">+</button>
                  </div>
                </div>

                <div className="action-buttons-container">
                  <button
                    className="add-to-cart-btn"
                    onClick={handleAddToCart}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>
                    {t('buttons.addToCart')}
                  </button>
                  <button
                    className="direct-order-btn"
                    onClick={handleDirectOrder}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><polyline points="17 11 19 13 23 9"></polyline></svg>
                    {t('buttons.directOrder') || 'অর্ডার করুন'}
                  </button>
                </div>

                <div className="secondary-actions">
                  <button
                    className="whatsapp-btn"
                    onClick={() => {
                      const whatsappNum = sessionStorage.getItem('whatsapp_number') || '8801741818807';
                      window.open(`https://wa.me/${whatsappNum}`, '_blank');
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.008-.57-.008-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.272-.57-.422l-.005.005zM12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm.012 19.822h-.005c-1.956 0-3.874-.527-5.55-1.52L6.46 18.3l-.933-2.616 2.766.732 1.643 1.054c1.536.936 3.483 1.026 5.564-.096 2.08-1.122 3.32-3.32 3.32-5.688 0-3.584-2.916-6.5-6.5-6.5-3.584 0-6.5 2.916-6.5 6.5 0 2.227 1.139 4.195 2.876 5.308l.385.247-.272 1.904-1.156 3.243 3.364-.882 1.895.272c.307.039.619.059.932.059 4.305 0 7.822-3.517 7.822-7.822 0-4.305-3.517-7.822-7.822-7.822z" />
                    </svg>
                    WhatsApp
                  </button>
                  <a
                    href={`tel:${sessionStorage.getItem('support_phone') || '+8801741818807'}`}
                    className="call-now-btn"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                    {t('buttons.callNow') || (currentLang === 'bn' ? 'কল করুন' : 'Call Now')}
                  </a>
                </div>

                <div className="trust-badges">
                  <div className="trust-badge">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                    <span>{currentLang === 'bn' ? 'নিরাপদ পেমেন্ট' : 'Secure Payment'}</span>
                  </div>
                  <div className="trust-badge">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>
                    <span>{currentLang === 'bn' ? 'দ্রুত ডেলিভারি' : 'Fast Delivery'}</span>
                  </div>
                  <div className="trust-badge">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2H3v16h5l4 4 4-4h5V2z"></path><path d="M12 11V7"></path><path d="M12 15h.01"></path></svg>
                    <span>{currentLang === 'bn' ? '২৪/৭ সাপোর্ট' : '24/7 Support'}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Full Product Description — only render when content exists */}
        {!isHtmlEmpty(productDesc) && (
          <section className="product-full-description">
            <h2>{currentLang === 'bn' ? 'বিস্তারিত বিবরণ' : 'Detailed Description'}</h2>
            <div
              className="description-content"
              dangerouslySetInnerHTML={{ __html: productDesc || '' }}
            />
          </section>
        )}

        {/* Action Buttons Below Description */}
        {product.in_stock && (
          <section className="description-action-buttons">
            <div className="action-buttons-row">
              <button
                className="add-to-cart-btn"
                onClick={handleAddToCart}
              >
                {t('buttons.addToCart')}
              </button>
              <button
                className="direct-order-btn"
                onClick={handleDirectOrder}
              >
                {t('buttons.directOrder') || 'অর্ডার করুন'}
              </button>
              <button
                className="whatsapp-btn"
                onClick={() => {
                  const whatsappNum = sessionStorage.getItem('whatsapp_number') || '8801741818807';
                  window.open(`https://wa.me/${whatsappNum}`, '_blank');
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '8px' }}>
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.008-.57-.008-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.272-.57-.422l-.005.005zM12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm.012 19.822h-.005c-1.956 0-3.874-.527-5.55-1.52L6.46 18.3l-.933-2.616 2.766.732 1.643 1.054c1.536.936 3.483 1.026 5.564-.096 2.08-1.122 3.32-3.32 3.32-5.688 0-3.584-2.916-6.5-6.5-6.5-3.584 0-6.5 2.916-6.5 6.5 0 2.227 1.139 4.195 2.876 5.308l.385.247-.272 1.904-1.156 3.243 3.364-.882 1.895.272c.307.039.619.059.932.059 4.305 0 7.822-3.517 7.822-7.822 0-4.305-3.517-7.822-7.822-7.822z" />
                </svg>
                WhatsApp
              </button>
              <a
                href={`tel:${sessionStorage.getItem('support_phone') || '+8801741818807'}`}
                className="call-now-btn"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '8px' }}>
                  <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 0 0-1.01.24l-1.57 1.97c-2.83-1.44-5.15-3.75-6.59-6.59l1.97-1.57c.26-.26.35-.65.24-1.01A11.36 11.36 0 0 1 8.59 4.2c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1zM12 3v10l3-3h6V3h-9z" />
                </svg>
                {t('buttons.callNow') || (currentLang === 'bn' ? 'কল করুন' : 'Call Now')}
              </a>
            </div>
          </section>
        )}

        {/* Related Products */}
        {product.related_products && product.related_products.length > 0 && (
          <section className="related-products">
            <h2>{t('product.relatedProducts')}</h2>
            <div className="products-grid">
              {product.related_products.map((relatedProduct) => (
                <ProductCard key={relatedProduct.id} product={relatedProduct} />
              ))}
            </div>
          </section>
        )}

        {/* Reviews Section */}
        <section className="reviews-section" id="reviews">
          <div className="reviews-header-bar">
            <div>
              <h2 className="reviews-title">{t('reviews.customerReviews')}</h2>
              <p className="reviews-subtitle">
                {currentLang === 'bn' ? 'আমাদের ক্রেতাদের সত ও নির্ভরযোগ্য রেটিং' : 'Verified ratings & reviews from customer feedback'}
              </p>
            </div>
            <button
              type="button"
              className="write-review-toggle-btn"
              onClick={() => setShowReviewForm(!showReviewForm)}
            >
              {showReviewForm
                ? (currentLang === 'bn' ? '✕ বন্ধ করুন' : '✕ Close Form')
                : (currentLang === 'bn' ? '✍️ রিভিউ লিখুন' : '✍️ Write a Review')}
            </button>
          </div>

          {showReviewForm && (
            <div className="review-form-collapsible-wrapper">
              <ReviewForm
                productId={product.id}
                onReviewSubmitted={() => {
                  loadReviews(product.id);
                  setShowReviewForm(false);
                }}
              />
            </div>
          )}

          <ReviewList reviews={reviews} loading={reviewsLoading} />
        </section>
      </div>
    </div>
  );
};

export default ProductDetails;
